import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import { BankAccountDashboardRow, BankReconciliationDashboard, BankReconciliationExceptionAgingRow, BankReconciliationVarianceRow } from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-reconciliation-dashboard',
    standalone: true,
    imports: [CommonModule, RouterModule, ButtonModule, TableModule, TagModule,
        PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Bank Reconciliation" title="Dashboard" subtitle="Operational KPIs for bank statement reconciliation, exceptions, variance, and account readiness.">
                <div actions>
                    <button pButton type="button" icon="pi pi-refresh" label="Refresh" [loading]="loading" (click)="load()"></button>
                    <button pButton type="button" icon="pi pi-list" label="Statements" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/statements'])"></button>
                    <button pButton type="button" icon="pi pi-chart-bar" label="Reports" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/reports'])"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <div class="quick-grid">
                <button pButton type="button" icon="pi pi-upload" label="Import Statement" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/statements/import'])"></button>
                <button pButton type="button" icon="pi pi-exclamation-triangle" label="Exception Queue" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/exceptions'])"></button>
                <button pButton type="button" icon="pi pi-wand-magic-sparkles" label="Rules" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/rules'])"></button>
                <button pButton type="button" icon="pi pi-wallet" label="Outstanding Items" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/reports/outstanding-items'])"></button>
            </div>

            <app-page-data-panel title="Bank Account Readiness" [resultText]="(dashboard?.bankAccounts?.length || 0) + ' accounts'">
                <p-table [value]="dashboard?.bankAccounts || []" [loading]="loading" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Bank Account</th><th>Last Reconciled</th><th>Open Statements</th><th>Open Sessions</th><th>Unmatched</th><th>Exceptions</th><th>Variance</th><th></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>
                                <strong>{{ row.accountName || ('Bank #' + row.bankAccountId) }}</strong>
                                <div class="muted">{{ row.accountNumber || '-' }} | {{ row.currencyCode || 'SAR' }}</div>
                            </td>
                            <td>{{ row.lastReconciledDate || 'Never' }}</td>
                            <td>{{ row.openStatementCount }}</td>
                            <td>{{ row.openSessionCount }}</td>
                            <td>{{ row.unmatchedLineCount }} / {{ amount(row.unmatchedAmount) }}</td>
                            <td><p-tag [value]="row.openExceptionCount" [severity]="row.openExceptionCount ? 'warn' : 'success'"></p-tag></td>
                            <td [class.negative]="row.varianceTotal">{{ amount(row.varianceTotal) }}</td>
                            <td class="actions"><button pButton type="button" icon="pi pi-search" label="Lines" [text]="true" (click)="openUnmatched(row)"></button></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="8">No reconciliation-enabled bank accounts found.</td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>

            <div class="panel-grid">
                <app-page-data-panel title="High Variance Sessions" [resultText]="(dashboard?.highVarianceSessions?.length || 0) + ' sessions'">
                    <p-table [value]="dashboard?.highVarianceSessions || []" [loading]="loading" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header"><tr><th>Session</th><th>Period</th><th>Variance</th><th>Status</th><th></th></tr></ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td>{{ row.statementNumber || ('Session #' + row.sessionId) }}<div class="muted">{{ row.bankAccountName || '-' }}</div></td>
                                <td>{{ row.periodStartDate || '-' }} to {{ row.periodEndDate || '-' }}</td>
                                <td [class.negative]="row.outsideTolerance">{{ amount(row.variance) }}</td>
                                <td><p-tag [value]="row.status" [severity]="row.outsideTolerance ? 'danger' : 'info'"></p-tag></td>
                                <td class="actions"><button pButton type="button" icon="pi pi-external-link" [text]="true" (click)="openSession(row)"></button></td>
                            </tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>

                <app-page-data-panel title="Exception Aging" [resultText]="(dashboard?.exceptionAging?.length || 0) + ' exceptions'">
                    <p-table [value]="dashboard?.exceptionAging || []" [loading]="loading" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header"><tr><th>Exception</th><th>Age</th><th>Severity</th><th>Status</th><th></th></tr></ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td>{{ row.exceptionType }}<div class="muted">{{ row.bankAccountName || '-' }}</div></td>
                                <td>{{ row.agingBucket }} ({{ row.ageDays }}d)</td>
                                <td>{{ row.severity }}</td>
                                <td><p-tag [value]="row.status" severity="warn"></p-tag></td>
                                <td class="actions"><button pButton type="button" icon="pi pi-external-link" [text]="true" *ngIf="row.sessionId" (click)="openException(row)"></button></td>
                            </tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>
            </div>
        </app-page-shell>
    `,
    styles: [`
        .quick-grid { display: flex; flex-wrap: wrap; gap: .65rem; margin-bottom: 1rem; }
        .panel-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
        .muted { color: #64748b; font-size: .78rem; margin-top: .2rem; }
        .actions { text-align: right; }
        .negative { color: #b91c1c; font-weight: 700; }
        @media (max-width: 900px) { .panel-grid { grid-template-columns: 1fr; } }
    `]
})
export class BankReconciliationDashboardComponent implements OnInit {
    dashboard?: BankReconciliationDashboard;
    metrics: PageMetric[] = [];
    loading = false;

    constructor(
        private service: BankReconciliationService,
        private sessionStorageService: SessionStorageService,
        public router: Router
    ) {}

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        this.loading = true;
        this.service.getDashboard(companyId).subscribe({
            next: dashboard => {
                this.dashboard = dashboard;
                this.metrics = [
                    { label: 'Open statements', value: dashboard.openStatementCount, tone: 'info' },
                    { label: 'Unreconciled accounts', value: dashboard.unreconciledBankAccountCount, tone: dashboard.unreconciledBankAccountCount ? 'warn' : 'success' },
                    { label: 'Unmatched amount', value: this.amount(dashboard.unmatchedAmount), tone: dashboard.unmatchedAmount ? 'warn' : 'success' },
                    { label: 'Open exceptions', value: dashboard.openExceptionCount, tone: dashboard.openExceptionCount ? 'danger' : 'success' },
                    { label: 'Last reconciled', value: dashboard.lastReconciledDate || 'Never', tone: 'default' }
                ];
                this.loading = false;
            },
            error: () => this.loading = false
        });
    }

    openUnmatched(row: BankAccountDashboardRow): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/reports/unmatched-bank-lines'], { queryParams: { bankAccountId: row.bankAccountId } });
    }

    openSession(row: BankReconciliationVarianceRow): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/sessions', row.sessionId]);
    }

    openException(row: BankReconciliationExceptionAgingRow): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/sessions', row.sessionId]);
    }

    amount(value?: number): string {
        return (value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
}
